// src/components/WaveformVisualizer.jsx
import React, { useEffect, useMemo, useRef } from "react";

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

const WaveformVisualizer = ({
  active,
  height = 44,
  width = 220,
  className = "",
}) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  const audioRef = useRef({
    stream: null,
    ctx: null,
    analyser: null,
    data: null,
    startedAt: 0,
  });

  const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;

  const gradientStops = useMemo(
    () => [
      { at: 0.0, color: "#f97316" }, // orange-500
      { at: 0.55, color: "#fb923c" }, // orange-400
      { at: 1.0, color: "#ef4444" }, // red-500
    ],
    []
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const W = Math.floor(width);
    const H = Math.floor(height);

    // Setup canvas for retina
    canvas.width = Math.floor(W * dpr);
    canvas.height = Math.floor(H * dpr);
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;

    const ctx2d = canvas.getContext("2d");
    if (!ctx2d) return;
    ctx2d.setTransform(dpr, 0, 0, dpr, 0, 0);

    const drawIdle = () => {
      ctx2d.clearRect(0, 0, W, H);

      // fond "glass" discret
      ctx2d.fillStyle = "rgba(255,255,255,0.35)";
      roundRect(ctx2d, 0, 0, W, H, 14);
      ctx2d.fill();

      // petite ligne centrale
      ctx2d.strokeStyle = "rgba(249,115,22,0.25)";
      ctx2d.lineWidth = 2;
      ctx2d.beginPath();
      ctx2d.moveTo(12, H / 2);
      ctx2d.lineTo(W - 12, H / 2);
      ctx2d.stroke();
    };

    const drawWave = () => {
      const a = audioRef.current;
      if (!a.analyser || !a.data) return;

      a.analyser.getByteTimeDomainData(a.data);

      ctx2d.clearRect(0, 0, W, H);

      // fond
      ctx2d.fillStyle = "rgba(255,255,255,0.55)";
      roundRect(ctx2d, 0, 0, W, H, 14);
      ctx2d.fill();

      // gradient stroke
      const g = ctx2d.createLinearGradient(0, 0, W, 0);
      for (const s of gradientStops) g.addColorStop(s.at, s.color);

      // waveform smoothing
      const mid = H / 2;
      const padX = 14;
      const usableW = W - padX * 2;

      // amplitude "pulse" global (style Shazam)
      const t = (performance.now() - a.startedAt) / 1000;
      const pulse = 0.92 + 0.08 * Math.sin(t * 2.6);

      ctx2d.lineWidth = 3;
      ctx2d.strokeStyle = g;
      ctx2d.shadowColor = "rgba(249,115,22,0.35)";
      ctx2d.shadowBlur = 10;

      ctx2d.beginPath();

      // on sample moins pour avoir une courbe "propre"
      const len = a.data.length;
      const points = 80; // plus grand => plus détaillé
      for (let i = 0; i < points; i++) {
        const idx = Math.floor((i / (points - 1)) * (len - 1));
        const v = a.data[idx] / 128.0; // ~1 au centre
        const y = mid + (v - 1) * (mid * 0.85) * pulse;
        const x = padX + (i / (points - 1)) * usableW;

        if (i === 0) ctx2d.moveTo(x, y);
        else {
          // courbe lissée
          const prevIdx = Math.floor(((i - 1) / (points - 1)) * (len - 1));
          const pv = a.data[prevIdx] / 128.0;
          const py = mid + (pv - 1) * (mid * 0.85) * pulse;
          const cx = (x + (padX + ((i - 1) / (points - 1)) * usableW)) / 2;
          const cy = (y + py) / 2;
          ctx2d.quadraticCurveTo(cx, cy, x, y);
        }
      }

      ctx2d.stroke();

      // baseline fine
      ctx2d.shadowBlur = 0;
      ctx2d.strokeStyle = "rgba(15,23,42,0.08)";
      ctx2d.lineWidth = 1;
      ctx2d.beginPath();
      ctx2d.moveTo(padX, mid);
      ctx2d.lineTo(W - padX, mid);
      ctx2d.stroke();

      rafRef.current = requestAnimationFrame(drawWave);
    };

    if (!active) {
      cancelAnimationFrame(rafRef.current);
      drawIdle();
      return;
    }

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true },
        });

        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const ctx = new AudioCtx();
        const source = ctx.createMediaStreamSource(stream);

        const analyser = ctx.createAnalyser();
        analyser.fftSize = 2048;
        analyser.smoothingTimeConstant = 0.88;

        source.connect(analyser);

        audioRef.current.stream = stream;
        audioRef.current.ctx = ctx;
        audioRef.current.analyser = analyser;
        audioRef.current.data = new Uint8Array(analyser.fftSize);
        audioRef.current.startedAt = performance.now();

        drawWave();
      } catch (e) {
        // si micro refusé, on reste en idle
        drawIdle();
      }
    };

    start();

    return () => {
      cancelAnimationFrame(rafRef.current);

      const a = audioRef.current;
      try {
        if (a.stream) a.stream.getTracks().forEach((t) => t.stop());
        if (a.ctx) a.ctx.close();
      } catch {}
      audioRef.current = { stream: null, ctx: null, analyser: null, data: null, startedAt: 0 };
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, width, height, dpr]);

  return (
    <canvas
      ref={canvasRef}
      className={`rounded-2xl border border-orange-200 bg-white/50 ${className}`}
      aria-hidden="true"
    />
  );
};

function roundRect(ctx, x, y, w, h, r) {
  const radius = clamp(r, 0, Math.min(w, h) / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

export default WaveformVisualizer;
